import { Box, useMediaQuery, useTheme } from '@mui/material';
import { v4 } from 'uuid';
import type { TWidget } from '../../utility/types/dataProvider';
import Widget from '../../components/Dashboard/Widget';
import { useDashboardContext } from './Dashboard';

export const getGridTemplateColumns = ({
  isChild,
  isMobileOrTablet,
  columns,
}: {
  isChild?: boolean;
  isMobileOrTablet: boolean;
  columns: string[] | undefined;
}): string => {
  // KPI bar any screen size.
  if (isChild) {
    return `repeat(${columns?.length || 0}, auto)`;
  }

  // If mobile/tablet just return a single column.
  if (isMobileOrTablet) {
    return '1fr';
  }

  // Map the columns from the dashboard layout.
  let mappedColumns = columns?.map((col: string) => `${col}fr`).join(' ') || '1fr';

  // Desktop dashboard
  return mappedColumns;
};

const DashboardLayout = () => {
  const { columns, dashboard, hasEqualHeightColumns, isChild, isDashboardMasked } =
    useDashboardContext();
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      className={`dashboard-layout`}
      sx={{
        display: isMobileOrTablet && !isChild ? 'flex' : 'grid',
        flexDirection: isMobileOrTablet && !isChild ? 'column' : 'inherit',
        flex: 1,
        gridTemplateColumns: getGridTemplateColumns({
          isChild,
          isMobileOrTablet,
          columns,
        }),
        gap: isMobileOrTablet && !isChild ? 0 : 1,
        overflowX: isMobileOrTablet && isChild ? 'auto' : 'visible',
        scrollSnapType: 'x mandatory',
        p: isMobileOrTablet && isChild ? 0 : 1,
        filter: isDashboardMasked ? 'blur(4px)' : 'none',
        '& > .kpi-card': {
          scrollSnapAlign: 'start',
        },
      }}
    >
      {!!columns?.length &&
        columns.map((col: string, i: number) => {
          const columnWidgets: TWidget[] | undefined = Array.isArray(
            dashboard?.layout?.[i],
          )
            ? (dashboard?.layout?.[i] as any[]).filter(
                (item): item is TWidget => typeof item === 'object' && item !== null,
              )
            : undefined;
          const colKey = `col-${col}-${i}`;

          return (
            <Box
              data-testid={colKey}
              key={colKey}
              className="dashboard-column"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: isChild ? 'auto' : 0,
              }}
            >
              {!!columnWidgets?.length &&
                columnWidgets?.map((widget: TWidget) => {
                  // If a module is not available, skip rendering the widget.
                  const isValidWidget = !!widget?.module && !!widget?.module?.data;
                  if (!isValidWidget) return null;

                  return (
                    <Widget
                      key={`widget-${widget?.id ?? v4()}-${i}`}
                      widget={widget}
                      hasEqualHeightColumns={hasEqualHeightColumns}
                    />
                  );
                })}
            </Box>
          );
        })}
    </Box>
  );
};

export default DashboardLayout;
